import { useState, useEffect, useCallback } from "react";
import { voteAPI } from "../lib/api";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { ScrollArea } from "./ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { Zap, Brain, Shield, AlertTriangle, Check, Users, Gavel, ChevronDown, ChevronUp, Trash } from "lucide-react";

const DECISION_ICONS = { delete: Trash, automate: Zap, augment: Brain, protect: Shield };
const DECISION_COLORS = {
  delete: "bg-red-50 text-red-700 border-red-200",
  automate: "bg-amber-50 text-amber-700 border-amber-200",
  augment: "bg-violet-50 text-violet-700 border-violet-200",
  protect: "bg-emerald-50 text-emerald-700 border-emerald-200",
};

export const VoteRevealView = ({ open, onOpenChange, boardId, isOwner, onBoardRefresh }) => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [expandedStall, setExpandedStall] = useState(null);
  const [overriding, setOverriding] = useState(null);

  const fetchResults = useCallback(async () => {
    try {
      const res = await voteAPI.getResults(boardId);
      setResults(res.data.results || []);
    } catch (err) {
      if (err.response?.status === 400) {
        toast.error("Votes haven't been revealed yet");
        onOpenChange(false);
      }
    } finally {
      setLoading(false);
    }
  }, [boardId, onOpenChange]);

  useEffect(() => {
    if (open) fetchResults();
  }, [open, fetchResults]);

  const handleOverride = async (stallId, decision) => {
    try {
      await voteAPI.override(boardId, stallId, { decision, notes: "Facilitator override after vote review" });
      toast.success("Decision overridden");
      setOverriding(null);
      fetchResults();
      onBoardRefresh?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Override failed");
    }
  };

  const filtered = results.filter((r) => {
    if (filter === "split") return r.is_split;
    if (filter === "consensus") return !r.is_split && r.total_votes > 0;
    if (filter === "no_votes") return r.total_votes === 0;
    return true;
  });

  const splitCount = results.filter((r) => r.is_split).length;
  const consensusCount = results.filter((r) => !r.is_split && r.total_votes > 0).length;
  const noVoteCount = results.filter((r) => r.total_votes === 0).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[85vh] p-0" data-testid="vote-reveal-dialog">
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-4">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Users className="w-5 h-5 text-indigo-500" />
              Vote Results
            </DialogTitle>
          </DialogHeader>

          <div className="flex items-center gap-2 mt-3 flex-wrap">
            {[
              { key: "all", label: `All (${results.length})` },
              { key: "split", label: `Split (${splitCount})`, color: "text-red-600" },
              { key: "consensus", label: `Consensus (${consensusCount})`, color: "text-emerald-600" },
              { key: "no_votes", label: `No Votes (${noVoteCount})`, color: "text-slate-400" },
            ].map((f) => (
              <button
                key={f.key}
                data-testid={`filter-${f.key}`}
                onClick={() => setFilter(f.key)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                  filter === f.key
                    ? "bg-indigo-50 border-indigo-200 text-indigo-700"
                    : "border-slate-200 text-slate-500 hover:border-slate-300"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        <ScrollArea className="max-h-[60vh] px-6 py-4">
          {loading ? (
            <div className="text-center py-8 text-slate-400">Loading results...</div>
          ) : (
            <div className="space-y-2">
              {filtered.map((result) => {
                const isExpanded = expandedStall === result.stall_id;
                const maxVotes = Math.max(result.votes.automate, result.votes.augment, result.votes.protect);
                const Icon = result.current_decision ? DECISION_ICONS[result.current_decision] : AlertTriangle;

                return (
                  <div
                    key={result.stall_id}
                    className={`bg-white rounded-xl border p-4 transition-colors ${
                      result.is_split ? "border-red-200 bg-red-50/30" : "border-slate-200"
                    }`}
                    data-testid={`vote-result-${result.stall_id}`}
                  >
                    <div
                      className="flex items-start justify-between cursor-pointer"
                      onClick={() => setExpandedStall(isExpanded ? null : result.stall_id)}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="font-heading font-medium text-sm text-slate-800">{result.stall_name}</h4>
                          {result.is_split && (
                            <Badge className="bg-red-100 text-red-700 border-red-200 text-[10px]">
                              <AlertTriangle className="w-2.5 h-2.5 mr-0.5" />
                              Split
                            </Badge>
                          )}
                          {result.majority && !result.is_split && (
                            <Badge className={`text-[10px] border ${DECISION_COLORS[result.majority]}`}>
                              <Check className="w-2.5 h-2.5 mr-0.5" />
                              {result.majority.charAt(0).toUpperCase() + result.majority.slice(1)}
                            </Badge>
                          )}
                        </div>
                        <span className="text-[10px] text-slate-400">{result.department}</span>
                      </div>

                      <div className="flex items-center gap-3">
                        {/* Vote bar visualization */}
                        {result.total_votes > 0 && (
                          <div className="flex items-center gap-1">
                            {["delete", "automate", "augment", "protect"].map((d) => {
                              const count = result.votes[d] || 0;
                              if (count === 0) return null;
                              const colors = { automate: "bg-amber-400", augment: "bg-violet-400", protect: "bg-emerald-400" };
                              return (
                                <div
                                  key={d}
                                  className={`h-5 rounded-sm ${colors[d]} flex items-center justify-center`}
                                  style={{ width: Math.max(20, (count / result.total_votes) * 80) }}
                                >
                                  <span className="text-[9px] text-white font-bold">{count}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        <span className="text-[10px] text-slate-400">{result.total_votes} votes</span>
                        {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-300" /> : <ChevronDown className="w-4 h-4 text-slate-300" />}
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="mt-3 pt-3 border-t border-slate-100 space-y-3 animate-fade-in">
                        {["delete", "automate", "augment", "protect"].map((d) => {
                          const voters = result.voters?.[d] || [];
                          if (voters.length === 0) return null;
                          const DIcon = DECISION_ICONS[d];
                          return (
                            <div key={d}>
                              <div className="flex items-center gap-2 mb-1.5">
                                <DIcon className="w-3.5 h-3.5 text-slate-500" />
                                <span className="text-xs font-semibold text-slate-700 capitalize">{d}</span>
                                <span className="text-[10px] text-slate-400">({voters.length})</span>
                              </div>
                              <div className="space-y-1 ml-5">
                                {voters.map((v, i) => (
                                  <div key={i} className="text-xs text-slate-600">
                                    <span className="font-medium">{v.user_name}</span>
                                    {v.rationale && <span className="text-slate-400 ml-1.5">— {v.rationale}</span>}
                                  </div>
                                ))}
                              </div>
                            </div>
                          );
                        })}

                        {isOwner && (
                          <div className="pt-2 border-t border-slate-100">
                            {overriding === result.stall_id ? (
                              <div className="flex items-center gap-2">
                                <Select onValueChange={(v) => handleOverride(result.stall_id, v)}>
                                  <SelectTrigger className="h-8 text-xs flex-1 bg-white">
                                    <SelectValue placeholder="Choose decision" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="delete">Delete</SelectItem>
                                    <SelectItem value="automate">Automate</SelectItem>
                                    <SelectItem value="augment">Augment</SelectItem>
                                    <SelectItem value="protect">Protect</SelectItem>
                                  </SelectContent>
                                </Select>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => setOverriding(null)}
                                  className="h-8 text-xs text-slate-400"
                                >
                                  Cancel
                                </Button>
                              </div>
                            ) : (
                              <Button
                                variant="outline"
                                size="sm"
                                data-testid={`override-btn-${result.stall_id}`}
                                onClick={() => setOverriding(result.stall_id)}
                                className="h-7 text-xs text-slate-500"
                              >
                                <Gavel className="w-3 h-3 mr-1" />
                                Override Decision
                              </Button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default VoteRevealView;
